import React, { Suspense, lazy } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { CartProvider } from "./Component/CartContext";
import Header from "./Component/Header";
import Footer from "./Component/Footer";
import ScrollToTop from "./Component/ScrollToTop";
import RouteLoader from "./Component/RouteLoader";
import AppErrorBoundary from "./Component/AppErrorBoundary";

const Main = lazy(() => import("./Component/Main"));
const Shop = lazy(() => import("./Component/Shop"));
const ProductDetail = lazy(() => import("./Component/ProductDetail"));
const CartPage = lazy(() => import("./Component/CartPage"));
const CheckOut = lazy(() => import("./Component/CheckOut"));
const PaySuccess = lazy(() => import("./Component/SuccessPay"));
const Recipes = lazy(() => import("./Component/Recipes"));
const RecipeDetail = lazy(() => import("./Component/RecipeDetail"));
const Wishlist = lazy(() => import("./Component/Wishlist"));
const Account = lazy(() => import("./Component/Account"));
const ExpressOrder = lazy(() => import("./Component/ExpressOrder"));
const About = lazy(() => import("./Component/About"));
const Contact = lazy(() => import("./Component/Contact"));
const Delivery = lazy(() => import("./Component/Delivery"));
const Returns = lazy(() => import("./Component/Returns"));
const Privacy = lazy(() => import("./Component/Privacy"));
const Terms = lazy(() => import("./Component/Terms"));
const FAQ = lazy(() => import("./Component/FAQ"));
const Cookies = lazy(() => import("./Component/Cookies"));
const Locations = lazy(() => import("./Component/Locations"));
const NotFound = lazy(() => import("./Component/NotFound"));

const Storefront = () => {
  const { pathname } = useLocation();
  const checkoutOnly = pathname === "/checkout";

  return (
    <>
      <ScrollToTop />
      {!checkoutOnly && <Header />}
      <div id="main-content" tabIndex="-1">
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/shop" element={<Shop />} />
            <Route
              path="/gifts"
              element={<Navigate to="/shop?category=gift-sets" replace />}
            />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/shopDetails/:id" element={<ProductDetail />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/recipes/:slug" element={<RecipeDetail />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/account" element={<Account />} />
            <Route path="/express-order" element={<ExpressOrder />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/cartpage" element={<Navigate to="/cart" replace />} />
            <Route path="/checkout" element={<CheckOut />} />
            <Route path="/order-success" element={<PaySuccess />} />
            <Route
              path="/paysuccess"
              element={<Navigate to="/order-success" replace />}
            />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/stores" element={<Navigate to="/locations" replace />} />
            <Route path="/delivery" element={<Delivery />} />
            <Route
              path="/shipping"
              element={<Navigate to="/delivery" replace />}
            />
            <Route path="/returns" element={<Returns />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!checkoutOnly && <Footer />}
    </>
  );
};

function App() {
  return (
    <AppErrorBoundary>
      <CartProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Storefront />
        </BrowserRouter>
      </CartProvider>
    </AppErrorBoundary>
  );
}

export default App;
